import PropTypes from "prop-types";
import React, { useCallback, useEffect, useState } from "react";
import { Form } from "semantic-ui-react";
import { RoomClasses } from "../../../../api/services/next";
import EditEntityPopup from '../../../../components/shared/EditEntityPopup';
import HotelsDropDown from '../../../../components/shared/HotelsDropDown';
import { onError, onSuccess, onWarning } from "../../../../components/shared/messages";
import RoomClassesDropDown from '../../../../components/shared/RoomClassesDropDown';
import useBoolean from "../../../../hooks/useBoolean";
import { useCurrentHotel } from "../../../../hooks/useCurrentHotel";
import { useTextResources } from '../../../../language';
import { useMessageResources } from '../../../../language';
import RoomClassGroupTransfer from './RoomClassGroupTransfer';

const defaultState = {
    Id: 0,
    Name: '',
    IsActive: true,
    RoomTypeIds: [],
};

const AddRoomClassPopup = ({
    onClose,
    controller,
    ...rest
}) => {
    const text = useTextResources();
    const message = useMessageResources();
    const currentHotel = useCurrentHotel();
    const isCreate = true;
    const {
        value: isLoading,
        setTrue: setLoading,
        setFalse: resetLoading
    } = useBoolean(false);

    const [roomClassGroup, setRoomClassGroupState] = useState(defaultState);
    const [hotelId, setHotelId] = useState(currentHotel?.HotelId)
    const [roomClasses, setRoomClasses] = useState([])
    const [selectedRoomClass, setSelectedRoomClass] = useState()
    const [open, setOpen] = useState(false);

    const getRoomClasses = useCallback(async () => {
        const roomClassesFromServer = await RoomClasses.GetRoomClasses({
            HotelId: hotelId
        })

        setRoomClasses(roomClassesFromServer);
    }, [hotelId])

    useEffect(() => {
        if (controller) {
            window[controller] = (action) => {
                switch (action) {
                    case 'open':
                        setOpen(true);
                        break;
                    case 'close':
                        setOpen(false);
                        break;
                    default:
                        break;
                }
            };
        }
    }, [controller]);

    useEffect(() => {
        if(hotelId > 0){
            getRoomClasses()
        } else {
            setRoomClasses([])
        }
    }, [hotelId, getRoomClasses])

    useEffect(() => {
        setRoomClassGroupState({
            ...defaultState,
        });
    }, [open]);

    useEffect(() => {
        setRoomClassGroupState(prev => ({
            ...prev,
            Id: selectedRoomClass?.Id,
            RoomClassName: selectedRoomClass?.Name,
            HotelId: hotelId
        }))
    }, [selectedRoomClass, hotelId])

    const setRoomClassGroups = useCallback((value) => {
        setRoomClassGroupState((prevState) => ({
            ...prevState,
            RoomTypeIds: value,
        }));
    }, []);

    const isValid = useCallback((roomClassGroup) => {
        if (!roomClassGroup.Id) {
            onWarning(message("AllFieldsAreRequired"));
            return false;
        }

        if (!roomClassGroup.RoomClassName) {
            onWarning(message("AllFieldsAreRequired"));
            return false;
        }

        if (!roomClassGroup.HotelId) {
            onWarning(message("AllFieldsAreRequired"));
            return false;
        }

        return true;
    }, [message]);

    const handleSave = useCallback(async () => {
        console.log('handleSave: ', roomClassGroup)
        if (!isValid(roomClassGroup)) {
            return false;
        }

        try {
            setLoading();
            await RoomClasses.SaveRoomClass(roomClassGroup);
            onSuccess();
            return true;
        } catch {
            onError()
            return false;
        } finally {
            resetLoading();
        }
    }, [roomClassGroup, resetLoading, setLoading]);

    const handleClose = useCallback(() => {
        if (onClose) onClose();
        setHotelId(undefined)
        setSelectedRoomClass(undefined)
        setRoomClassGroupState(undefined)
        setOpen(false);
    }, [onClose])

    const handleSaveAndClose = useCallback(async () => {
        const isSaved = await handleSave();
        if (isSaved) {
            handleClose();
        }
    }, [handleSave, handleClose]);

    const onAddRoomClass = useCallback(async (name) => {
        const roomClass = await RoomClasses.SaveRoomClass({
            RoomClassName: name
        });

        if (roomClass) {
            setRoomClasses(prev => [...prev, roomClass]);
            setSelectedRoomClass(roomClass)
        }
    }, [])

    return (
        <EditEntityPopup
            isCreate={isCreate}
            header={text("SaveRoomClass")}
            onClose={handleClose}
            onSave={handleSave}
            onSaveAndClose={handleSaveAndClose}
            isLoading={isLoading}
            {...rest}
            open={open}
        >
            <Form>
                <Form.Field
                    key='HotelId'
                    label={text('Hotel')}
                    control={HotelsDropDown}
                    onChange={setHotelId}
                    placeholder={text('SelectHotel')}
                    value={hotelId}
                    clearable
                    required
                />
                <Form.Field
                    key='RoomClassId'
                    disabled={!hotelId}
                    label={text('RoomClass')}
                    data={roomClasses}
                    control={RoomClassesDropDown}
                    onChangeItem={setSelectedRoomClass}
                    placeholder={text('SelectRoomClass')}
                    onAddItem={(_e, { value }) => onAddRoomClass(value)}
                    value={selectedRoomClass?.Id}
                    allowAdditions
                    clearable
                    required
                />
                <Form.Field
                    label={text("RoomTypes")}
                    control={RoomClassGroupTransfer}
                    hotelId={hotelId}
                    roomClassId={selectedRoomClass?.Id}
                    onChange={setRoomClassGroups}
                    value={roomClassGroup?.RoomTypeIds}

                />
            </Form>
        </EditEntityPopup>
    );
};

AddRoomClassPopup.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    controller: PropTypes.string,
};

AddRoomClassPopup.defaultProps = {
    open: false,
    onClose: () => { },
}

export default AddRoomClassPopup;